import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Gamepad2, Search, Tags, ChevronDown, Pencil, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, isRespAdmin } from "@/context/AuthContext";
import { BottomSheet, Empty, DeleteButton, Field, BulkCheck, BulkBar, ConfirmAction, COLORI_SQUADRA, blurSuTouch } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

// Chip categoria colorato, riusato su card e dettaglio
function CategoriaBadge({ categoria, className = "" }) {
  if (!categoria) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${className}`}
      style={{ backgroundColor: `${categoria.colore}22`, color: categoria.colore }}
      data-testid={`categoria-badge-${categoria.id}`}
    >
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: categoria.colore }} />
      {categoria.nome}
    </span>
  );
}

export default function Giochi() {
  const { user } = useAuth();
  const canEdit = isRespAdmin(user);
  const [giochi, setGiochi] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [sel, setSel] = useState(null);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", descrizione: "", regole: "", scala: "", categoria_id: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);

  const load = useCallback(() => {
    api.get("/giochi").then((r) => setGiochi(r.data));
    api.get("/categorie-giochi").then((r) => setCategorie(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const parseScala = (s) => {
    if (!s.trim()) return null;
    const nums = s.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));
    return nums.length ? nums : null;
  };

  const apri = (g = null) => {
    if (g) {
      setSel(g);
      setForm({ nome: g.nome, descrizione: g.descrizione || "", regole: g.regole || "", scala: g.scala_punti ? g.scala_punti.join(", ") : "", categoria_id: g.categoria?.id || "" });
    } else {
      setSel(null);
      setForm({ nome: "", descrizione: "", regole: "", scala: "", categoria_id: "" });
    }
    setNuovoOpen(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { nome: form.nome, descrizione: form.descrizione, regole: form.regole, scala_punti: parseScala(form.scala), categoria_id: form.categoria_id || null };
    try {
      if (sel) await api.put(`/giochi/${sel.id}`, payload);
      else await api.post("/giochi", payload);
      toast.success("Gioco salvato");
      setNuovoOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/giochi/${sel.id}`);
      toast.success("Gioco eliminato");
      setNuovoOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const [dettaglio, setDettaglio] = useState(null);

  return (
    <div data-testid="giochi-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Giochi</h1>
          <p className="text-sm text-slate-500 font-semibold">Catalogo dei giochi dell'oratorio</p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            {giochi.length > 0 && (
              <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => { if (bulkMode) { setBulkMode(false); setSelected([]); } else setBulkMode(true); }} data-testid="giochi-bulk-toggle">
                {bulkMode ? "Annulla selezione" : "Seleziona"}
              </button>
            )}
            <button className="btn-primary" onClick={() => apri()} data-testid="nuovo-gioco-btn">
              <Plus className="h-4 w-4" /> Nuovo gioco
            </button>
          </div>
        )}
      </div>

      {canEdit && <GestioneCategorie categorie={categorie} onChange={load} />}

      {bulkMode && (
        <BulkBar count={selected.length} total={giochi.length} testId="giochi-bulk-bar"
          onSelectAll={() => setSelected(selected.length === giochi.length ? [] : giochi.map((g) => g.id))}
          onClear={() => { setBulkMode(false); setSelected([]); }}
          actions={
            <ConfirmAction onConfirm={async () => {
              try {
                const { data } = await api.post("/giochi/bulk/delete", { ids: selected });
                toast.success(`${data.deleted} giochi eliminati`);
                setBulkMode(false); setSelected([]); load();
              } catch (err) { toast.error(errMsg(err)); }
            }} title={`Eliminare ${selected.length} giochi?`} testId="giochi-bulk-delete-confirm">
              <button type="button" disabled={selected.length === 0} className="btn-danger disabled:opacity-40 !py-2" data-testid="giochi-bulk-delete-btn">
                Elimina {selected.length > 0 && `(${selected.length})`}
              </button>
            </ConfirmAction>
          } />
      )}

      {giochi.length === 0 ? (
        <Empty icon={Gamepad2} text="Nessun gioco nel catalogo" sub={canEdit ? "Aggiungi il primo gioco" : "I Responsabili aggiungeranno presto i giochi"} />
      ) : (
        <>
          <div className="relative mb-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="search" className="input-app !pl-11" placeholder="Cerca gioco per nome o descrizione..." value={search} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setSearch(e.target.value)} data-testid="giochi-search-input" />
          </div>
          {(() => {
            const q = search.trim().toLowerCase();
            const lista = q ? giochi.filter((g) => `${g.nome} ${g.descrizione || ""} ${g.regole || ""}`.toLowerCase().includes(q)) : giochi;
            if (lista.length === 0) return (
              <div className="card-app p-6 text-center"><p className="text-sm text-slate-400 font-semibold">Nessun gioco trovato per {`"${search}"`}</p></div>
            );
            return (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {lista.map((g, i) => {
                  const isSel = selected.includes(g.id);
                  const toggle = () => setSelected((s) => s.includes(g.id) ? s.filter((x) => x !== g.id) : [...s, g.id]);
                  return (
                    <motion.div key={g.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }}
                      className={`card-app p-5 text-left cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all ${bulkMode && isSel ? "ring-2 ring-amber-400" : ""}`}
                      onClick={() => (bulkMode ? toggle() : canEdit ? apri(g) : setDettaglio(g))} data-testid={`gioco-card-${g.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        {bulkMode && <BulkCheck checked={isSel} onClick={toggle} testId={`gioco-check-${g.id}`} />}
                        <Gamepad2 className="h-6 w-6 text-sky-500 mb-2" />
                      </div>
                      <p className="font-display font-extrabold text-slate-900">{g.nome}</p>
                      <p className="text-xs text-slate-400 font-semibold line-clamp-2">{g.descrizione || "Nessuna descrizione"}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {g.categoria && <CategoriaBadge categoria={g.categoria} />}
                        {g.scala_punti && <span className="badge-info">Scala personalizzata</span>}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}

      <BottomSheet open={nuovoOpen} onOpenChange={setNuovoOpen} title={sel ? `Modifica: ${sel.nome}` : "Nuovo gioco"} testId="gioco-sheet">
        <form onSubmit={salva}>
          <Field label="Nome"><input className="input-app" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="gioco-nome-input" /></Field>
          <Field label="Categoria (facoltativa)" hint={categorie.length === 0 ? "Crea le categorie dalla card «Categorie» in alto" : "Serve per le classifiche per categoria"}>
            <select className="input-app" value={form.categoria_id}
              onChange={(e) => { setForm({ ...form, categoria_id: e.target.value }); blurSuTouch(e); }}
              data-testid="gioco-categoria-select">
              <option value="">Nessuna categoria</option>
              {categorie.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </Field>
          <Field label="Descrizione"><textarea className="input-app" rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="gioco-descrizione-input" /></Field>
          <Field label="Regole"><textarea className="input-app" rows={4} value={form.regole} onChange={(e) => setForm({ ...form, regole: e.target.value })} data-testid="gioco-regole-input" /></Field>
          <Field label="Scala punti personalizzata (facoltativa)" hint="Punti per posizione separati da virgola, es. 20, 15, 10, 5, 3, 1. Vuoto = scala di default">
            <input className="input-app" value={form.scala} onChange={(e) => setForm({ ...form, scala: e.target.value })} data-testid="gioco-scala-input" />
          </Field>
          <button className="btn-primary w-full mt-2" disabled={saving} data-testid="gioco-submit-btn">{saving ? "Salvataggio..." : "Salva gioco"}</button>
          {sel && (
            <div className="mt-3 flex justify-end">
              <DeleteButton onConfirm={elimina} entity={`il gioco ${sel.nome}`} testId="gioco-delete-btn" />
            </div>
          )}
        </form>
      </BottomSheet>

      <BottomSheet open={!!dettaglio} onOpenChange={(v) => !v && setDettaglio(null)} title={dettaglio?.nome} testId="gioco-detail-sheet">
        {dettaglio && (
          <div className="space-y-4 text-sm">
            {dettaglio.categoria && <div><CategoriaBadge categoria={dettaglio.categoria} /></div>}
            <div><p className="label-app">Descrizione</p><p className="text-slate-700 font-medium whitespace-pre-wrap">{dettaglio.descrizione || "—"}</p></div>
            <div><p className="label-app">Regole</p><p className="text-slate-700 font-medium whitespace-pre-wrap">{dettaglio.regole || "—"}</p></div>
            {dettaglio.scala_punti && <div><p className="label-app">Scala punti</p><p className="text-slate-700 font-medium">{dettaglio.scala_punti.join(" · ")}</p></div>}
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

/**
 * Card espandibile per gestire le categorie dei giochi (nome + colore).
 * Le categorie abilitano le classifiche per categoria.
 */
function GestioneCategorie({ categorie, onChange }) {
  const [aperta, setAperta] = useState(false);
  const [edit, setEdit] = useState(null); // { id?, nome, colore }
  const [saving, setSaving] = useState(false);

  const apri = (c = null) =>
    setEdit(c ? { id: c.id, nome: c.nome, colore: c.colore } : { nome: "", colore: COLORI_SQUADRA[0] });

  const salva = async (e) => {
    e.preventDefault();
    if (!edit.nome.trim()) return toast.error("Dai un nome alla categoria");
    setSaving(true);
    try {
      const payload = { nome: edit.nome.trim(), colore: edit.colore };
      if (edit.id) await api.put(`/categorie-giochi/${edit.id}`, payload);
      else await api.post("/categorie-giochi", payload);
      toast.success("Categoria salvata");
      setEdit(null);
      onChange();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/categorie-giochi/${edit.id}`);
      toast.success("Categoria eliminata");
      setEdit(null);
      onChange();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="card-app mb-5 overflow-hidden" data-testid="categorie-panel">
      <button type="button" onClick={() => setAperta((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/60 transition-colors"
        aria-expanded={aperta} data-testid="categorie-toggle">
        <Tags className="h-4 w-4 text-amber-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-800 text-sm">Categorie dei giochi</p>
          {!aperta && (
            <p className="text-xs text-slate-400 font-semibold truncate">
              {categorie.length === 0 ? "Nessuna categoria · tocca per crearne" : categorie.map((c) => c.nome).join(" · ")}
            </p>
          )}
        </div>
        <span className="text-xs font-bold text-slate-400 shrink-0">{categorie.length}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${aperta ? "rotate-180" : ""}`} />
      </button>

      {aperta && (
        <div className="px-4 pb-4">
          {categorie.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {categorie.map((c) => (
                <button key={c.id} type="button" onClick={() => apri(c)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: `${c.colore}22`, color: c.colore }}
                  data-testid={`categoria-chip-${c.id}`}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.colore }} />
                  {c.nome}
                  <Pencil className="h-3 w-3 opacity-60" />
                </button>
              ))}
            </div>
          )}
          <button type="button" className="btn-ghost !py-2 text-xs" onClick={() => apri()} data-testid="nuova-categoria-btn">
            <Plus className="h-3.5 w-3.5" /> Nuova categoria
          </button>
        </div>
      )}

      <BottomSheet open={!!edit} onOpenChange={(v) => { if (!v) setEdit(null); }}
        title={edit?.id ? "Modifica categoria" : "Nuova categoria"} testId="categoria-sheet">
        {edit && (
          <form onSubmit={salva}>
            <Field label="Nome">
              <input className="input-app" required value={edit.nome}
                onChange={(e) => setEdit({ ...edit, nome: e.target.value })}
                placeholder="Es. Sportivi, Acqua, Enigmistici..." data-testid="categoria-nome-input" />
            </Field>
            <Field label="Colore">
              <div className="flex flex-wrap gap-2">
                {COLORI_SQUADRA.map((col) => (
                  <button key={col} type="button" onClick={() => setEdit({ ...edit, colore: col })}
                    className={`h-9 w-9 rounded-full transition-transform ${edit.colore === col ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : ""}`}
                    style={{ backgroundColor: col }} aria-label={`Colore ${col}`} data-testid={`categoria-colore-${col}`} />
                ))}
              </div>
            </Field>
            <button className="btn-primary w-full" disabled={saving} data-testid="categoria-submit-btn">
              {saving ? "Salvataggio..." : "Salva categoria"}
            </button>
            {edit.id && (
              <div className="mt-3 flex justify-end">
                <DeleteButton onConfirm={elimina} entity={`la categoria ${edit.nome}`} testId="categoria-delete-btn" />
              </div>
            )}
          </form>
        )}
      </BottomSheet>
    </div>
  );
}
